// Library & collections (PLAN §5.0, §5.0b; task 9b): pure rules shared by the upload screen, the app API, the
// pipeline and their tests. Folder paths are data: they only ever name collections, never storage keys.

import type { AttributionLevel, ClaimKind } from './claims';
import { normalizeName } from './names';
import type { DetectedKind, ExtractionDepth, PipelineStatus } from './uploads';

// ── Collections from folders ────────────────────────────────────────────────────────────────────────────────

const segments = (path: string) =>
  path
    .replace(/\\/g, '/')
    .split('/')
    .map(s => s.trim())
    .filter(s => s && s !== '.' && s !== '..');

/**
 * The dropped folder that only wraps other folders: when every file of a drop sits under one top folder and that
 * folder has sub-folders (e.g. `datasample/interviews/…`), the sub-folders become the collections and the top folder
 * is just the container. A single folder without sub-folders (`interviews/…`) is itself the collection.
 */
export function containerFolderOf(paths: string[]): string | null {
  const split = paths.map(segments).filter(s => s.length > 0);
  if (!split.length) return null;
  const top = split[0]?.[0];
  if (!top || !split.every(s => s.length >= 2 && s[0] === top)) return null;
  return split.some(s => s.length >= 3) ? top : null;
}

/**
 * The folders a file sits in, outermost first, below the container folder: each becomes a (nested) collection.
 * Loose files and files directly inside the container belong to no collection (they are still in the Library).
 */
export function collectionFoldersOf(path: string, containerFolder?: string | null): string[] {
  const s = segments(path).slice(0, -1);
  if (containerFolder && s[0] === containerFolder) s.shift();
  return s.map(f => f.slice(0, 200));
}

/** Same collection whatever the case or accents ("Interviews" = "interviews"). */
export const collectionKey = (name: string) => normalizeName(name).slice(0, 200) || name.trim().toLowerCase();

/** How a collection's content is treated by default (PLAN §5.0b "Collection defaults"). */
export interface CollectionDefaults {
  extractionDepth: ExtractionDepth | null;
  claimKind: ClaimKind | null;
  /** Information credibility 1–6 (6 = cannot be judged). */
  credibility: number | null;
  attribution: AttributionLevel | null;
}

export const NO_DEFAULTS: CollectionDefaults = { extractionDepth: null, claimKind: null, credibility: null, attribution: null };

/**
 * Defaults proposed for a new folder collection. Interviews: read at Claims & mentions (D372), and what interviewees
 * say is an allegation, "doubtful until corroborated" (credibility 5), following the sample set's own reading guide.
 * Everything else: none.
 */
export function collectionPreset(name: string): CollectionDefaults {
  if (/interview|entretien|مقابل/i.test(name))
    return { ...NO_DEFAULTS, extractionDepth: 'claims', claimKind: 'allegation', credibility: 5 };
  return NO_DEFAULTS;
}

/**
 * A note that reads as a collection's reading guide ("how to read interviews", "README", "method"): its kind is a
 * guide note and its name says so. A short note alone is not enough (every small text file is a guide note).
 */
export function looksLikeReadingGuide(filename: string, kind?: DetectedKind): boolean {
  const base = (filename.split(/[\\/]/).pop() ?? filename).replace(/\.[^.]+$/, '').toLowerCase();
  return (kind === undefined || kind === 'guide_note') && /guide|how[ _-]to|read[ _-]?me|method|mode d.emploi/.test(base);
}

// ── Extraction depth ─────────────────────────────────────────────────────────────────────────────────────────

/** Shallowest first: "raise depth later" only ever moves right. */
export const DEPTH_ORDER: ExtractionDepth[] = ['library', 'mentions', 'claims', 'full'];
export const depthRank = (d: ExtractionDepth) => DEPTH_ORDER.indexOf(d);
export const deeperDepths = (d: ExtractionDepth) => DEPTH_ORDER.slice(depthRank(d) + 1);

export const EXTRACTION_DEPTH_HELP: Record<ExtractionDepth, string> = {
  full: 'Entities, facts, relations, events and ratings. For profile sheets, actor lists and datasets.',
  claims:
    'Entities, the relations and money flows between them, and the statements, allegations and assessments made about them, with their sources (minor details like websites are skipped). For interviews and their transcripts, syntheses, articles and reports.',
  mentions: 'Only which entities each passage mentions, so profiles show "Mentioned in…". For long notes and background reports.',
  library: 'Nothing extracted. Fully searchable and used by the chat.',
};

/**
 * Statuses from which a document's depth can be raised: it is back to rest in the Library. Not while it uploads,
 * waits for an answer or is being processed (the running pipeline would be overtaken).
 */
export const RAISE_DEPTH_STATUSES: PipelineStatus[] = ['library_only', 'extracted', 'in_review', 'completed', 'skipped_review', 'failed'];

// ── Document links ───────────────────────────────────────────────────────────────────────────────────────────

export const DOCUMENT_LINK_TYPES = ['summarizes', 'profiles', 'newer_version_of', 'related'] as const;
export type DocumentLinkType = (typeof DOCUMENT_LINK_TYPES)[number];
export const isDocumentLinkType = (v: unknown): v is DocumentLinkType => DOCUMENT_LINK_TYPES.includes(v as DocumentLinkType);

/** How a link reads from each end: "summarizes → T3" on the synthesis, "summarized by ← synthesis" on T3. */
export const DOCUMENT_LINK_LABELS: Record<DocumentLinkType, { from: string; to: string; help: string }> = {
  summarizes: {
    from: 'Summarizes',
    to: 'Summarized in',
    help: 'An interview synthesis and the transcripts it cites ([T3, 27:43]).',
  },
  profiles: { from: 'Profiles', to: 'Profiled in', help: 'A profile sheet and the actor list it details a row of.' },
  newer_version_of: {
    from: 'Newer version of',
    to: 'Older version of',
    help: 'A later version of the same document, such as an updated profile sheet.',
  },
  related: { from: 'Related to', to: 'Related to', help: 'Any other connection worth following.' },
};

// ── Mentions ─────────────────────────────────────────────────────────────────────────────────────────────────

export const MENTION_STATUSES = ['suggested', 'confirmed', 'rejected'] as const;
export type MentionStatus = (typeof MENTION_STATUSES)[number];

/** Longest name looked up in a passage, in words. */
export const MENTION_MAX_WORDS = 5;

/**
 * Every run of 1–5 consecutive words of a passage, in the folded form names are stored in (accents, case and
 * Arabic spelling variants ignored). A name matches a passage when its folded form is one of these.
 */
export function mentionGrams(text: string, maxWords = MENTION_MAX_WORDS): string[] {
  const words = normalizeName(text).split(' ').filter(Boolean);
  const out = new Set<string>();
  for (let i = 0; i < words.length; i++)
    for (let n = 1; n <= maxWords && i + n <= words.length; n++) out.add(words.slice(i, i + n).join(' '));
  return [...out];
}

/**
 * Whether a matched name is specific enough to count as a mention: several words, or one word of 4+ letters
 * (short single words — "EU", "Le" — match too much text). Confidence is lower for single words.
 */
export function mentionConfidence(folded: string): number | null {
  const words = folded.split(' ').filter(Boolean);
  if (words.length >= 2) return 0.9;
  if ((words[0]?.length ?? 0) >= 4) return 0.7;
  return null;
}

/** A short passage around the first place a name occurs, for "Mentioned in…" and search results. */
export function snippetAround(text: string, needles: string[], radius = 110): string {
  const lower = text.toLowerCase();
  let at = -1;
  let len = 0;
  for (const n of needles) {
    const i = n ? lower.indexOf(n.toLowerCase()) : -1;
    if (i >= 0 && (at < 0 || i < at)) {
      at = i;
      len = n.length;
    }
  }
  if (at < 0) return text.length > radius * 2 ? `${text.slice(0, radius * 2).trimEnd()}…` : text;
  const start = Math.max(0, at - radius);
  const end = Math.min(text.length, at + len + radius);
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`;
}

/** Query words for passage search: 2+ characters, at most 8. */
export function searchWords(q: string): string[] {
  return [
    ...new Set(
      q
        .toLowerCase()
        .split(/[^\p{L}\p{N}]+/u)
        .filter(w => w.length >= 2),
    ),
  ].slice(0, 8);
}

/** Kinds offered as Library filters, in a sensible reading order. */
export const LIBRARY_KINDS: DetectedKind[] = [
  'profile_sheet',
  'actor_list',
  'transcript',
  'interview_synthesis',
  'narrative_report',
  'guide_note',
  'dataset',
  'media',
  'image',
  'other',
];
